// 🚀 API utility functions for video processing
import { env } from '@/config/env';
import { createApiHeaders } from './user';

// Base URL for Cloudflare Worker - Now using environment config
const getCFWorkerUrl = (): string => {
  // 🚀 FIX: Remove trailing slash to prevent double slashes in API calls
  const cleanUrl = env.CF_WORKER_URL.replace(/\/$/, '');
  console.log('🔍 getCFWorkerUrl debug:', {
    original: env.CF_WORKER_URL,
    cleaned: cleanUrl,
    hasTrailingSlash: env.CF_WORKER_URL.endsWith('/')
  });
  return cleanUrl;
};

// 🚀 Warm-up API functions
export const warmupSrtServer = async (): Promise<any> => {
  try {
    console.log('🔥 Starting SRT server warm-up...');
    const response = await fetch(`${getCFWorkerUrl()}/api/warmup/srt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const result = await response.json();
    console.log('🔥 Warm-up response:', result);
    return result;
  } catch (error) {
    console.error('🔥 Warm-up failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Payment API functions
export const createPaymentOrder = async (orderData: any): Promise<any> => {
  const headers = createApiHeaders();
  
  const response = await fetch(`${getCFWorkerUrl()}/api/payment/create-order`, {
    method: 'POST',
    headers,
    body: JSON.stringify(orderData),
  });
  return response.json();
};

export const verifyPayment = async (orderId: string): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/payment/verify`, {
    method: 'POST',
    headers: createApiHeaders(),
    body: JSON.stringify({ order_id: orderId }),
  });
  return response.json();
};

export const capturePayment = async (captureData: any): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/payment/capture`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(captureData),
  });
  return response.json();
};

// Video progress API functions
export const getVideoProgress = async (videoId: string): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/job/status?video_id=${videoId}`);
  return response.json();
};

export const getProgress = async (videoId: string, jobId?: string): Promise<any> => {
  const params = new URLSearchParams({ video_id: videoId });
  if (jobId) {
    params.append('job_id', jobId);
  }
  const response = await fetch(`${getCFWorkerUrl()}/api/job/status?${params.toString()}`, {
    method: 'GET',
    headers: createApiHeaders(),
  });
  return response.json();
};

// Video processing API functions - Updated to use process-full-video with payment verification
export const startFullProcessing = async (videoId: string, executeRecaptcha?: (action: string) => Promise<string>): Promise<any> => {
  // 🚀 Check database first before calling RunPod
  console.log('🔍 startFullProcessing - Checking database status first...');
  try {
    const currentStatus = await getProgress(videoId);
    console.log('📊 startFullProcessing - Current database status:', currentStatus);
    
    // Check if full processing is already completed
    if (currentStatus.status === 'completed') {
      console.log('✅ Full processing already completed, returning existing data');
      return currentStatus;
    }
    
    // If still processing, return current status
    if (currentStatus.status === 'full_processing') {
      console.log('⏳ startFullProcessing - Full processing still in progress');
      return {
        success: true,
        status: 'still_processing',
        data: currentStatus,
        message: 'Full processing still in progress'
      };
    }
    
  } catch (dbError) {
    console.log('⚠️ startFullProcessing - Database check failed, proceeding with RunPod:', dbError);
  }
  
  // If not completed in database, proceed with RunPod
  console.log('📤 startFullProcessing - Proceeding with RunPod API call...');
  
  // Get fresh reCAPTCHA token for this API call
  let recaptchaToken: string | undefined;
  try {
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('video_processing');
      console.log('✅ Fresh reCAPTCHA token obtained for start full processing');
    }
  } catch (error) {
    console.error('❌ Failed to get reCAPTCHA token for start full processing:', error);
    throw new Error('reCAPTCHA verification failed');
  }
  
  const headers = createApiHeaders();
  
  if (recaptchaToken) {
    (headers as any)['X-Recaptcha-Token'] = recaptchaToken;
  }
  
  // Updated: Call process-full-video endpoint with payment verification
  const response = await fetch(`${getCFWorkerUrl()}/api/process-full-video`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ 
      video_id: videoId,
      require_payment: true // Enable payment verification
    }),
  });
  return response.json();
};

// S3 and upload API functions
export const getS3PresignedUrls = async (uploadData: any, executeRecaptcha?: (action: string) => Promise<string>): Promise<any> => {
  // Get fresh reCAPTCHA token for this API call
  let recaptchaToken: string | undefined;
  try {
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('file_upload');
      console.log('✅ Fresh reCAPTCHA token obtained for S3 presigned URLs');
    }
  } catch (error) {
    console.error('❌ Failed to get reCAPTCHA token for S3 presigned URLs:', error);
    throw new Error('reCAPTCHA verification failed');
  }
  
  const headers = createApiHeaders();
  
  if (recaptchaToken) {
    (headers as any)['X-Recaptcha-Token'] = recaptchaToken;
  }
  
  const response = await fetch(`${getCFWorkerUrl()}/api/s3/presigned-urls`, {
    method: 'POST',
    headers,
    body: JSON.stringify(uploadData),
  });
  
  if (!response.ok) {
    // Try to parse error response for detailed message
    let errorMessage = `Failed to get presigned URLs: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
        alert(errorMessage);

      }
      if (errorData.job_in_progress && errorData.job_details) {
        errorMessage = `${errorData.error}\n\nJob in progress: ${errorData.job_details.type} (${errorData.job_details.status})\nVideo ID: ${errorData.job_details.videoId}`;
        // Show browser alert for job in progress error
      }
    } catch (e) {
      // If parsing fails, use default message
    }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Translation API functions
export const startTranslation = async (translationData: any, executeRecaptcha?: (action: string) => Promise<string>): Promise<any> => {
  // Get fresh reCAPTCHA token for this API call
  let recaptchaToken: string | undefined;
  try {
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('translation');
      console.log('✅ Fresh reCAPTCHA token obtained for translation start');
    }
  } catch (error) {
    console.error('❌ Failed to get reCAPTCHA token for translation start:', error);
    throw new Error('reCAPTCHA verification failed');
  }
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (recaptchaToken) {
    (headers as any)['X-Recaptcha-Token'] = recaptchaToken;
  }
  
  const response = await fetch(`${getCFWorkerUrl()}/api/translate/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(translationData),
  });
  return response.json();
};

// Download API functions
export const getDownloadLinks = async (videoId: string, type: string): Promise<any> => {
  const params = new URLSearchParams({ video_id: videoId, type });
  
  const response = await fetch(`${getCFWorkerUrl()}/api/download/links?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json();
};

// Translate SRT subtitles
export const translateSrt = async (srtUrl: string, targetLanguage: string, sourceLanguage: string = 'auto', videoId?: string, executeRecaptcha?: (action: string) => Promise<string>): Promise<{ translated_srt_url: string }> => {
  console.log('🌐 translateSrt - Starting translation:', {
    srtUrl,
    targetLanguage,
    sourceLanguage
  });

  try {
    // Get fresh reCAPTCHA token for this API call
    let recaptchaToken: string | undefined;
    try {
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('translation');
        console.log('✅ Fresh reCAPTCHA token obtained for SRT translation');
      }
    } catch (error) {
      console.error('❌ Failed to get reCAPTCHA token for SRT translation:', error);
      throw new Error('reCAPTCHA verification failed');
    }
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (recaptchaToken) {
      headers['X-Recaptcha-Token'] = recaptchaToken;
    }
    
    const response = await fetch('https://xyz2db21w8.execute-api.eu-central-1.amazonaws.com/default/translate-srt', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        srt_url: srtUrl,
        target_language: targetLanguage,
        source_language: sourceLanguage,
        video_id: videoId
      })
    });

    // 🚀 DEBUG: Log the actual URL being called
    const apiUrl = 'https://xyz2db21w8.execute-api.eu-central-1.amazonaws.com/sub/translate-srt';
    console.log('🔍 translateSrt - API URL debug:', {
      baseUrl: 'AWS Lambda',
      endpoint: '/sub/translate-srt',
      fullUrl: apiUrl,
      hasDoubleSlash: apiUrl.includes('//api'),
      original: 'AWS Lambda Endpoint'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Translation API error:', response.status, errorText);
      throw new Error(`Translation failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ translateSrt - Translation successful:', result);
    
    return result;
  } catch (error) {
    console.error('❌ translateSrt - Translation failed:', error);
    throw error;
  }
};

// Support API functions
export const submitSupportTicket = async (ticketData: any): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/support/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ticketData),
  });
  return response.json();
};

// Utility function to check if API is accessible
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${getCFWorkerUrl()}/health`);
    return response.ok;
  } catch (error) {
    console.error('❌ API Health Check Failed:', error);
    return false;
  }
};

